import { ApolloError } from '@apollo/client';
import classNames from 'classnames/bind';
import { useState } from 'react';
import { Checkmark, Copy, Error as ErrorIcon } from '../../assets/icons/Icons';
import { useEnv, useUser } from '../../context/PortalContext/PortalContext';
import Button from '../Button/Button';
import NoPermission from '../NoPermission/NoPermission';
import Tooltip from '../Tooltip/Tooltip';
import styles from './error.module.scss';

const cx = classNames.bind(styles);

/** The server refusing the caller: a page they may not open, not a fault to report. */
export const isRefusal = (error: ErrorProps['error']) =>
  error instanceof ApolloError && error.graphQLErrors.some((graphQLError) => graphQLError.extensions?.code === 'FORBIDDEN');

const formatObject = (obj) => {
  if (!obj) {
    return null;
  }
  if (typeof obj === 'string') {
    return obj;
  }
  return JSON.stringify(obj, null, 2).replace(/['"]+/g, '');
};

interface ErrorProps {
  hasNav?: boolean;
  href?: string;
  title?: string;
  details?: string;
  inline?: boolean;
  error?: null | Error | ApolloError;
  additionalInfo?: string | unknown;
  hideCopy?: boolean;
  ctaText?: string;
}

export const ErrorDisplay = ({
  hasNav = false,
  href = '/',
  title = 'It looks like something went wrong',
  details = 'Please try again in a few minutes or refresh the page.',
  inline,
  error,
  additionalInfo,
  hideCopy,
  ctaText,
}: ErrorProps) => {
  const [tooltipContent, setTooltipContent] = useState({ text: 'Copy to clipboard', icon: Copy });
  const user = useUser();
  const env = useEnv();

  // A page's own query refused is the app door held without the page's key. Inline displays sit inside a
  // page the caller can open, so a full-page refusal would claim more than the server said.
  if (!inline && isRefusal(error)) {
    return <NoPermission hasNav={hasNav} href={href} />;
  }

  const errorMessage = `ERROR DETAILS
${
  user?.details
    ? `From: ${user.details.nameFirst} ${user.details.nameLast}
Email: ${user.details.email}`
    : 'User not logged in'
}
==============================
Current location: ${window.location.href}
Environment: ${env}
User Agent: ${window.navigator.userAgent}
===============================
Current user:
${user?.details ? formatObject(user.details) : 'No user details'}
===============================
${
  error
    ? error instanceof ApolloError
      ? `Error:
${formatObject(error)}
===============================`
      : `Error:
name: ${error.name}
message: ${error.message}
stack: ${error.stack}
===============================`
    : ''
}
${
  additionalInfo
    ? `Additional Info:
${formatObject(additionalInfo)}
`
    : ''
}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(errorMessage);
    setTooltipContent({ text: 'Copied!', icon: Checkmark });
  };

  return (
    <div className={cx('error', 'page-bg', { 'has-nav': hasNav, 'error--inline': inline })} data-testid='error'>
      {ErrorIcon}
      <h1 className={cx('error__title')}>{title}</h1>
      <p className={cx('error__details')}>
        {details}
        {!hideCopy && (
          <>
            {' '}
            <Tooltip content={tooltipContent.text}>
              <button aria-label='copy' onClick={handleCopy} className={cx('error__copy')}>
                {tooltipContent.icon}
              </button>
            </Tooltip>
          </>
        )}
      </p>
      <Button href={href} type='link-primary' size='large'>
        {ctaText || 'Return to Dashboard'}
      </Button>
    </div>
  );
};

export default ErrorDisplay;
